import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Shield, CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp,
  Settings, Zap, Package, Info, Loader
} from 'lucide-react';
import { useAlert } from '../context/AlertContext';

/**
 * Tenant Module Management Component
 *
 * SOC 2 Controls:
 * - CC6.1: Feature-level access control configuration
 * - CC6.2: RBAC integration with modules
 * - CC6.3: Tenant feature segregation
 * - CC6.6: Configuration change logging
 *
 * Allows platform admins to manage module access for tenants
 */
const TenantModuleManagement = ({ tenantId, tenantName, onUpdate }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [modules, setModules] = useState([]);
  const [mainModules, setMainModules] = useState([]);
  const [subModules, setSubModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [selectedPlan, setSelectedPlan] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [togglingModules, setTogglingModules] = useState(new Set());

  useEffect(() => {
    if (tenantId) {
      fetchTenantModules();
    }
  }, [tenantId]);

  const fetchTenantModules = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('platform_admin_token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/saarv-admin/tenants/${tenantId}/modules`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const allModules = response.data.modules || [];
      setModules(allModules);

      // Separate main and sub modules
      const main = allModules.filter(m => m.module_type === 'main');
      const sub = allModules.filter(m => m.module_type === 'sub');

      setMainModules(main);
      setSubModules(sub);

      // Auto-expand enabled main modules
      const enabledMain = new Set(main.filter(m => m.is_enabled).map(m => m.slug));
      setExpandedModules(enabledMain);
    } catch (error) {
      console.error('Error fetching tenant modules:', error);
      showError('Error loading modules: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = async (moduleSlug, currentStatus, skipSubModules = false) => {
    // Add to toggling set to show loading state on this specific module
    setTogglingModules(prev => new Set(prev).add(moduleSlug));

    try {
      const token = localStorage.getItem('platform_admin_token');
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/saarv-admin/tenants/${tenantId}/modules/${moduleSlug}`,
        { is_enabled: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Update the module state directly without refetching (prevents screen flicker)
        const newStatus = !currentStatus;

        // Find the module being toggled before updating state
        const toggledModule = [...mainModules, ...subModules].find(m => m.slug === moduleSlug);

        console.log(`Toggle ${moduleSlug}: ${currentStatus} -> ${newStatus}`, toggledModule);

        setModules(prevModules => {
          const updated = prevModules.map(m =>
            m.slug === moduleSlug ? { ...m, is_enabled: newStatus } : m
          );
          console.log('Updated modules:', updated.filter(m => m.slug === moduleSlug));
          return updated;
        });

        setMainModules(prevMain => {
          const updated = prevMain.map(m =>
            m.slug === moduleSlug ? { ...m, is_enabled: newStatus } : m
          );
          return updated;
        });

        setSubModules(prevSub => {
          const updated = prevSub.map(m =>
            m.slug === moduleSlug ? { ...m, is_enabled: newStatus } : m
          );
          return updated;
        });

        // If enabling a main module, automatically enable all its sub-modules and auto-expand
        if (!skipSubModules && newStatus === true && toggledModule?.module_type === 'main') {
          console.log(`Enabling main module ${moduleSlug}, will auto-enable sub-modules`);

          // Auto-expand the main module to show sub-modules
          setExpandedModules(prev => {
            const newSet = new Set(prev);
            newSet.add(moduleSlug);
            console.log('Expanded modules:', Array.from(newSet));
            return newSet;
          });

          // Get current sub-modules that need to be enabled
          const currentSubMods = subModules.filter(
            sub => sub.parent_module === moduleSlug && !sub.is_enabled
          );

          console.log(`Found ${currentSubMods.length} sub-modules to enable:`, currentSubMods.map(s => s.slug));

          // Enable each sub-module sequentially
          for (const subModule of currentSubMods) {
            console.log(`Enabling sub-module: ${subModule.slug}`);
            await toggleModule(subModule.slug, false, true); // skipSubModules=true to avoid recursion
          }
        }

        if (onUpdate) onUpdate();
      } else {
        // Show error message from response
        showError(response.data.error || 'Failed to toggle module');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || error.message;
      showError('Error toggling module: ' + errorMessage);
    } finally {
      // Remove from toggling set
      setTogglingModules(prev => {
        const newSet = new Set(prev);
        newSet.delete(moduleSlug);
        return newSet;
      });
    }
  };

  const bulkUpdateByPlan = async () => {
    if (!selectedPlan) {
      showWarning('Please select a subscription plan');
      return;
    }

    const confirmed = await confirm(
      'Confirm Plan Update',
      `Set modules for ${tenantName} based on ${selectedPlan.toUpperCase()} plan?`,
      { confirmLabel: 'Apply', destructive: false }
    );

    if (!confirmed) {
      return;
    }

    try {
      setBulkUpdating(true);
      const token = localStorage.getItem('platform_admin_token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/saarv-admin/tenants/${tenantId}/modules/bulk-update`,
        { plan: selectedPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showSuccess(`Successfully enabled modules for ${selectedPlan} plan:\n- ${response.data.enabled_modules.join('\n- ')}`);
      await fetchTenantModules();
      if (onUpdate) onUpdate();
    } catch (error) {
      showError('Error bulk updating modules: ' + (error.response?.data?.detail || error.message));
    } finally {
      setBulkUpdating(false);
    }
  };

  const toggleExpanded = (moduleSlug) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleSlug)) {
      newExpanded.delete(moduleSlug);
    } else {
      newExpanded.add(moduleSlug);
    }
    setExpandedModules(newExpanded);
  };

  const getSubModulesForParent = (parentSlug) => {
    return subModules.filter(m => m.parent_module === parentSlug);
  };

  const getPlanBadgeColor = (plans) => {
    if (!plans || plans.length === 0) return 'bg-gray-100 text-gray-700';
    if (plans.includes('free')) return 'bg-green-100 text-green-800';
    if (plans.includes('starter')) return 'bg-blue-100 text-blue-800';
    if (plans.includes('professional')) return 'bg-purple-100 text-purple-800';
    if (plans.includes('enterprise')) return 'bg-orange-100 text-orange-800';
    return 'bg-gray-100 text-gray-700';
  };

  const getHighestPlan = (plans) => {
    if (!plans || plans.length === 0) return 'N/A';
    if (plans.includes('enterprise')) return 'Enterprise';
    if (plans.includes('professional')) return 'Professional';
    if (plans.includes('starter')) return 'Starter';
    if (plans.includes('free')) return 'Free';
    return 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 text-blue-600 animate-spin" />
        <span className="ml-3 text-gray-600">Loading modules...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Bulk Actions */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Module Access Control</h3>
              <p className="text-sm text-gray-600">Manage feature access for {tenantName}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm font-semibold text-gray-700">
              {modules.filter(m => m.is_enabled).length} / {modules.length} Enabled
            </span>
          </div>
        </div>

        {/* Bulk Update by Plan */}
        <div className="bg-white rounded-lg p-4 border border-blue-300">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            <Zap className="w-4 h-4 inline mr-1" />
            Quick Setup by Subscription Plan
          </label>
          <div className="flex space-x-2">
            <select
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={bulkUpdating}
            >
              <option value="">Select Plan...</option>
              <option value="free">Free (Core modules only)</option>
              <option value="starter">Starter (HIRA, PTW, CAPA)</option>
              <option value="professional">Professional (All Safety + Energy)</option>
              <option value="enterprise">Enterprise (All modules)</option>
            </select>
            <button
              onClick={bulkUpdateByPlan}
              disabled={bulkUpdating || !selectedPlan}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {bulkUpdating ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  <span>Apply Plan</span>
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            <Info className="w-3 h-3 inline mr-1" />
            This will automatically enable modules based on the selected subscription plan
          </p>
        </div>
      </div>

      {/* Main Modules with Sub-modules */}
      <div className="space-y-4">
        {mainModules
          .sort((a, b) => (a.order || 999) - (b.order || 999))
          .map((mainModule) => {
            const subMods = getSubModulesForParent(mainModule.slug);
            const isExpanded = expandedModules.has(mainModule.slug);
            const enabledSubCount = subMods.filter(m => m.is_enabled).length;

            return (
              <div key={mainModule.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                {/* Main Module Header */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <button
                        onClick={() => toggleExpanded(mainModule.slug)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-600" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-600" />
                        )}
                      </button>

                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h4 className="text-lg font-bold text-gray-900">{mainModule.name}</h4>
                          {mainModule.is_core && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                              CORE
                            </span>
                          )}
                          {!mainModule.is_core && (
                            <span className={`px-2 py-1 text-xs font-semibold rounded ${getPlanBadgeColor(mainModule.plans)}`}>
                              {getHighestPlan(mainModule.plans)}+
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{mainModule.description || 'Main module'}</p>
                        {subMods.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            {enabledSubCount} / {subMods.length} sub-modules enabled
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={mainModule.is_enabled}
                          onChange={() => toggleModule(mainModule.slug, mainModule.is_enabled)}
                          disabled={mainModule.is_core || togglingModules.has(mainModule.slug)}
                          className="sr-only peer"
                        />
                        <div className={`w-14 h-7 bg-gray-300 rounded-full peer peer-checked:bg-green-500 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed transition-colors`}>
                          <div className={`absolute top-0.5 left-0.5 bg-white w-6 h-6 rounded-full shadow transform transition-transform ${mainModule.is_enabled ? 'translate-x-7' : ''}`} />
                        </div>
                      </label>
                      {togglingModules.has(mainModule.slug) ? (
                        <Loader className="w-6 h-6 text-blue-600 animate-spin" />
                      ) : mainModule.is_enabled ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : (
                        <XCircle className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Sub-modules (expandable) */}
                {isExpanded && subMods.length > 0 && (
                  <div className="p-4 space-y-2 bg-gray-50">
                    {subMods.map((subModule) => (
                      <div key={subModule.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <Shield className="w-4 h-4 text-blue-600" />
                              <h5 className="font-semibold text-gray-900">{subModule.name}</h5>
                              {subModule.plans && subModule.plans.length > 0 && (
                                <span className={`px-2 py-0.5 text-xs font-semibold rounded ${getPlanBadgeColor(subModule.plans)}`}>
                                  {getHighestPlan(subModule.plans)}+
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1 ml-6">{subModule.description || 'Sub-module'}</p>
                            {subModule.dependencies && subModule.dependencies.length > 1 && (
                              <p className="text-xs text-gray-500 mt-1 ml-6">
                                <AlertCircle className="w-3 h-3 inline mr-1" />
                                Requires: {subModule.dependencies.filter(d => d !== subModule.parent_module).join(', ')}
                              </p>
                            )}
                          </div>

                          <div className="flex items-center space-x-3 ml-4">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={subModule.is_enabled}
                                onChange={() => toggleModule(subModule.slug, subModule.is_enabled)}
                                disabled={togglingModules.has(subModule.slug)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-blue-500 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed transition-colors">
                                <div className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow transform transition-transform ${subModule.is_enabled ? 'translate-x-5' : ''}`} />
                              </div>
                            </label>
                            {togglingModules.has(subModule.slug) ? (
                              <Loader className="w-5 h-5 text-blue-600 animate-spin" />
                            ) : subModule.is_enabled ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : (
                              <XCircle className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Show message if no sub-modules */}
                {isExpanded && subMods.length === 0 && (
                  <div className="p-4 bg-gray-50 text-center text-sm text-gray-500">
                    No sub-modules available for this main module
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {/* SOC 2 Compliance Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-blue-900">SOC 2 Compliance</p>
            <p className="text-xs text-blue-700 mt-1">
              All module configuration changes are logged for audit purposes (CC6.6, CC7.2).
              Feature-level access control enforces tenant segregation (CC6.1, CC6.3).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantModuleManagement;
